/**
 * Dynamic-import wrapper for `country-state-city`.
 *
 * Rationale: the underlying JSON data is ~300KB uncompressed. We don't want
 * it in the main bundle since it's only needed once a user reaches the
 * Contact page. Loading it via a dynamic import puts it in its own webpack
 * chunk that CRA lazy-loads on first use.
 *
 * `loadGeo()` is idempotent — subsequent callers get the cached module
 * synchronously (Promise resolves instantly).
 *
 * The `*Sync` helpers below let downstream code (form validation, payload
 * transformation) treat the geo library as synchronously-available AS LONG
 * AS someone has awaited `loadGeo()` earlier. If the library hasn't been
 * loaded yet they degrade gracefully — returning empty lists or the raw
 * ISO code — rather than throwing.
 */

let _csc = null;

export async function loadGeo() {
    if (!_csc) {
        // Selectively import only country + state modules — city.json alone is
        // 7.7MB uncompressed (2.2MB gzipped), so we keep the city field as
        // free-text to preserve a lean lazy chunk (~150KB gzipped).
        // Each submodule uses `export default` (see lib/country.js /
        // lib/state.js), so we read `.default`.
        const [countryMod, stateMod] = await Promise.all([
            import("country-state-city/lib/country"),
            import("country-state-city/lib/state"),
        ]);
        _csc = {
            Country: countryMod.default,
            State: stateMod.default,
        };
    }
    return _csc;
}

export function getStatesOfCountrySync(countryIso) {
    if (!_csc || !countryIso) return [];
    return _csc.State.getStatesOfCountry(countryIso);
}

// City data is intentionally excluded from the geo chunk (see loadGeo above).
// The city input is a free-text field. This helper is retained as a stub so
// downstream conditional logic can uniformly ask "does this state have cities?"
// and always get an empty list — driving the fallback to free-text.
export function getCitiesOfStateSync() {
    return [];
}

export function countryNameSync(countryIso) {
    if (!_csc || !countryIso) return countryIso || "";
    return _csc.Country.getCountryByCode(countryIso)?.name || countryIso;
}

export function stateNameSync(stateIso, countryIso) {
    if (!_csc || !stateIso || !countryIso) return stateIso || "";
    return (
        _csc.State.getStateByCodeAndCountry(stateIso, countryIso)?.name ||
        stateIso
    );
}

export function getCountryByCodeSync(countryIso) {
    if (!_csc || !countryIso) return null;
    return _csc.Country.getCountryByCode(countryIso);
}
